import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { sizedImage, sizedSrcSet } from '../../utils/imageSize';

// Rotating soft tints behind the round photos.
const TINTS = ['bg-tint-blush', 'bg-tint-butter', 'bg-tint-mint', 'bg-tint-sky', 'bg-tint-lilac'];

const U = (id) => `https://images.unsplash.com/${id}?w=300&q=70&auto=format`;

// The six real category slugs, then real pages (sale, new in, brands,
// collections, gifting, stylist).
export const CATEGORY_TILES = [
  { key: 'women', to: '/products?cat=women', label: { en: 'Women', ar: 'نساء' }, image: U('photo-1490481651871-ab68de25d43d') },
  { key: 'men', to: '/products?cat=men', label: { en: 'Men', ar: 'رجال' }, image: U('photo-1617137968427-85924c800a22') },
  { key: 'bags', to: '/products?cat=bags', label: { en: 'Bags', ar: 'حقائب' }, image: U('photo-1548036328-c9fa89d128fa') },
  { key: 'jewelry', to: '/products?cat=jewelry', label: { en: 'Jewellery', ar: 'مجوهرات' }, image: 'https://images.pexels.com/photos/1232931/pexels-photo-1232931.jpeg?w=300&q=70&auto=format' },
  { key: 'perfumes', to: '/products?cat=perfumes', label: { en: 'Fragrance', ar: 'عطور' }, image: U('photo-1541643600914-78b084683601') },
  { key: 'accessories', to: '/products?cat=accessories', label: { en: 'Accessories', ar: 'إكسسوارات' }, image: U('photo-1590874103328-eac38a683ce7') },
  { key: 'new', to: '/products', label: { en: 'New in', ar: 'وصل حديثًا' }, image: U('photo-1539008835657-9e8e9680c956') },
  { key: 'sale', to: '/products?sale=true', label: { en: 'Sale', ar: 'تخفيضات' }, image: U('photo-1584917865442-de89df76afd3') },
  { key: 'brands', to: '/brands', label: { en: 'Brands', ar: 'العلامات' }, image: U('photo-1543163521-1bf539c55dd2') },
  { key: 'collections', to: '/collections', label: { en: 'Edits', ar: 'التشكيلات' }, image: U('photo-1515562141207-7a88fb7ce338') },
  { key: 'gifts', to: '/corporate-gifting', label: { en: 'Gifts', ar: 'هدايا' }, image: U('photo-1513201099705-a9746e1e201f') },
  { key: 'stylist', to: '/stylist', label: { en: 'Stylist', ar: 'منسقة الأزياء' }, image: U('photo-1509631179647-0177331693ae') },
];

/** Round photo tiles on soft tints: two rows that scroll sideways on phones.
 *  Desktop (lg): one row of square photo tiles with a small radius on the
 *  neutral well, like a store's category strip (no story circles). */
const HomeCategoryTiles = () => {
  const { t } = useLanguage();
  return (
    <section className="pt-4 lg:luxury-stage-wide lg:pt-8" aria-labelledby="home-cat-tiles">
      <h2 id="home-cat-tiles" className="sr-only">{t({ en: 'Shop by category', ar: 'تسوق حسب الفئة' })}</h2>
      <ul className="anum-no-scrollbar grid auto-cols-[78px] grid-flow-col grid-rows-2 gap-x-3 gap-y-3 overflow-x-auto px-3 sm:auto-cols-[92px] lg:grid-flow-row lg:grid-cols-12 lg:grid-rows-1 lg:gap-x-4 lg:overflow-visible lg:px-0">
        {CATEGORY_TILES.map((tile, i) => (
          <li key={tile.key}>
            <Link to={tile.to} className="anum-rise group flex flex-col items-center gap-1.5 rounded-xl p-0.5 text-center">
              <span className={`relative block aspect-square w-full overflow-hidden rounded-full lg:rounded-2xl lg:bg-well ${TINTS[i % TINTS.length]}`}>
                <img
                  src={sizedImage(tile.image, 200)}
                  srcSet={sizedSrcSet(tile.image, [160, 240, 320])}
                  sizes="(min-width: 1024px) 7vw, (min-resolution: 2.5dppx) 53px, 80px"
                  alt=""
                  width="200"
                  height="200"
                  loading={i < 8 ? 'eager' : 'lazy'}
                  decoding="async"
                  onError={(event) => { event.currentTarget.style.visibility = 'hidden'; }}
                  className="absolute inset-[7%] h-[86%] w-[86%] rounded-full object-cover transition-transform duration-300 motion-safe:group-hover:scale-105 lg:inset-0 lg:h-full lg:w-full lg:rounded-none"
                />
              </span>
              <span className={`block text-[13px] font-semibold leading-tight lg:text-[14px] ${tile.key === 'sale' ? 'text-crimson-ink' : 'text-foreground'}`}>{t(tile.label)}</span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
};

export default HomeCategoryTiles;
